import { MeterStatusThresholdDraft } from "@/shared/models/MeterStatusThreshold";
import {
	getMeterStatusBands,
	METER_STATUS_LABELS,
	MeterStatusLevel,
	MeterStatusMetric,
} from "@/shared/utils/meterStatusLevels";
import { Box, Tooltip, Typography } from "@mui/material";
import { useRef, useState } from "react";

const formatThreshold = (value: number): string =>
	value.toLocaleString("en-US", { maximumFractionDigits: 2 });

export const metrics = {
	Lag: {
		unit: "h",
		scaleMax: 96,
		step: 24,
	},
	Coverage: {
		unit: "%",
		scaleMax: 100,
		step: 25,
	},
};

type Props = {
	drafts: MeterStatusThresholdDraft[];
	metric: MeterStatusMetric;
	disabled: boolean;
	onBoundaryChange: (level: MeterStatusLevel, value: number) => void;
};

export const MeterStatusScale = ({
	drafts,
	metric,
	disabled,
	onBoundaryChange,
}: Props) => {
	const trackRef = useRef<HTMLDivElement>(null);

	const [draggedLevel, setDraggedLevel] = useState<MeterStatusLevel | null>(
		null,
	);

	const metricProperties = metrics[metric];
	const bands = getMeterStatusBands(drafts, metric);

	const unit = metricProperties.unit;
	const scaleMax = metricProperties.scaleMax;
	const step = metricProperties.step;
	const ticks = Array.from(
		{ length: Math.floor(scaleMax / step) + 1 },
		(_, index) => index * step,
	);

	const valueAt = (clientX: number) => {
		const track = trackRef.current?.getBoundingClientRect();
		if (track === undefined || track.width === 0) return 0;
		const ratio = (clientX - track.left) / track.width;
		return Math.round(Math.min(1, Math.max(0, ratio)) * scaleMax);
	};

	return (
		<Box>
			<Box ref={trackRef} sx={{ height: 34, position: "relative" }}>
				<Box
					sx={{
						borderRadius: 1,
						display: "flex",
						height: "100%",
						overflow: "hidden",
					}}
				>
					{bands.map((band, index) => {
						const from = band.Threshold;
						const to = bands[index + 1]?.Threshold ?? scaleMax;

						return (
							<Tooltip
								key={band.Level}
								title={`${METER_STATUS_LABELS[band.Level]}: ${formatThreshold(from)}${unit} to ${
									bands[index + 1] === undefined
										? "the top"
										: `${formatThreshold(to)}${unit}`
								}`}
							>
								<Box
									sx={{
										alignItems: "center",
										bgcolor: band.BackgroundColor,
										display: "flex",
										justifyContent: "center",
										minWidth: 3,
										px: 0.5,
										width: `${Math.min(100, Math.max(0, ((to - from) / scaleMax) * 100))}%`,
									}}
								>
									<Typography
										sx={{
											color: band.Color,
											fontSize: "0.75rem",
											fontWeight: 600,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{METER_STATUS_LABELS[band.Level]}
									</Typography>
								</Box>
							</Tooltip>
						);
					})}
				</Box>

				{bands.slice(1).map((band) => (
					<Box
						key={band.Level}
						aria-hidden
						onPointerDown={(event) => {
							if (disabled) return;
							event.currentTarget.setPointerCapture(event.pointerId);
							setDraggedLevel(band.Level);
						}}
						onPointerMove={(event) => {
							if (draggedLevel !== band.Level) return;
							onBoundaryChange(band.Level, valueAt(event.clientX));
						}}
						onPointerUp={(event) => {
							event.currentTarget.releasePointerCapture(event.pointerId);
							setDraggedLevel(null);
						}}
						sx={{
							alignItems: "center",
							bottom: -3,
							cursor: disabled ? "default" : "ew-resize",
							display: "flex",
							justifyContent: "center",
							left: `${Math.min(100, (band.Threshold / scaleMax) * 100)}%`,
							ml: "-8px",
							position: "absolute",
							top: -3,
							touchAction: "none",
							width: 16,
							zIndex: draggedLevel === band.Level ? 2 : 1,
							"&:hover > div": { transform: disabled ? "none" : "scaleX(1.5)" },
						}}
					>
						<Box
							sx={{
								bgcolor: draggedLevel === band.Level ? "#333" : "#666",
								border: "1px solid #fff",
								borderRadius: 1,
								boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
								height: "100%",
								transition: "transform 0.1s, background-color 0.1s",
								width: 4,
							}}
						/>
					</Box>
				))}
			</Box>

			<Box sx={{ height: 16, pt: 0.5, position: "relative" }}>
				{ticks.map((tick, index) => (
					<Typography
						key={tick}
						sx={{
							color: "text.secondary",
							fontSize: "0.65rem",
							fontVariantNumeric: "tabular-nums",
							left: `${(tick / scaleMax) * 100}%`,
							position: "absolute",
							transform:
								index === 0
									? "none"
									: index === ticks.length - 1
										? "translateX(-100%)"
										: "translateX(-50%)",
						}}
					>
						{tick}
						{unit}
					</Typography>
				))}
			</Box>
		</Box>
	);
};

export const describeMeterStatusBands = (
	drafts: MeterStatusThresholdDraft[],
	metric: MeterStatusMetric,
): string => {
	const bands = getMeterStatusBands(drafts, metric);
	const unit = metric === "Lag" ? "h" : "%";

	return bands
		.map((band, index) => {
			const label = METER_STATUS_LABELS[band.Level];
			const from = formatThreshold(band.Threshold);
			const next = bands[index + 1];
			if (next === undefined) return `${label}: ${from}${unit} or more`;

			const to = formatThreshold(next.Threshold);
			return index === 0
				? `${label}: under ${to}${unit}`
				: `${label}: ${from} to under ${to}${unit}`;
		})
		.join(" · ");
};
